import type { Dispatch, FormEvent, SetStateAction } from "react";
import { PhoneInput } from "react-international-phone";
import Button from "../../../shared/components/Button";
import PasswordInput from "../../../shared/components/PasswordInput";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatCpf } from "../../../shared/utils/cpf";
import {
  type CreateUserPayload,
  USER_CATEGORY_VALUES,
  userCategoryLabel,
} from "../../auth/shared/types/auth";

type AdminCreateUserModalProps = {
  isAdmin: boolean;
  newUser: CreateUserPayload;
  onCancel: () => void;
  onDismiss: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  setNewUser: Dispatch<SetStateAction<CreateUserPayload>>;
};

export default function AdminCreateUserModal({
  isAdmin,
  newUser,
  onCancel,
  onDismiss,
  onSubmit,
  setNewUser,
}: AdminCreateUserModalProps) {
  const t = siteTexts.admin;
  useBodyScrollLock(true);

  return (
    <div
      className="admin-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t.modal.addUserAriaLabel}
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        onDismiss();
      }}
    >
      <div className="admin-modal">
        <div className="admin-modal-header">
          <h3>{t.modal.addUserTitle}</h3>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <label className="field">
            <span>{t.labels.name}</span>
            <input
              type="text"
              value={newUser.name}
              onChange={(event) =>
                setNewUser((prev) => ({ ...prev, name: event.target.value }))
              }
              required
            />
          </label>
          <label className="field">
            <span>{t.labels.email}</span>
            <input
              type="email"
              value={newUser.email}
              onChange={(event) =>
                setNewUser((prev) => ({ ...prev, email: event.target.value }))
              }
              required
            />
          </label>
          <label className="field">
            <span>{t.labels.password}</span>
            <PasswordInput
              value={newUser.password}
              onChange={(event) =>
                setNewUser((prev) => ({
                  ...prev,
                  password: event.target.value,
                }))
              }
              required
            />
          </label>
          <label className="field">
            <span>{t.labels.cpf}</span>
            <input
              type="text"
              value={newUser.cpf}
              onChange={(event) =>
                setNewUser((prev) => ({
                  ...prev,
                  cpf: formatCpf(event.target.value),
                }))
              }
              placeholder="000.000.000-00"
              inputMode="numeric"
              maxLength={14}
              required
            />
          </label>
          <label className="field">
            <span>{t.labels.phone}</span>
            <PhoneInput
              defaultCountry="br"
              value={newUser.phone}
              onChange={(value) => setNewUser((prev) => ({ ...prev, phone: value }))}
              placeholder="+55 (11) 99999-9999"
              required
            />
          </label>
          <label className="field">
            <span>{t.labels.sex}</span>
            <select
              value={newUser.sex}
              onChange={(event) =>
                setNewUser((prev) => ({
                  ...prev,
                  sex: event.target.value as CreateUserPayload["sex"],
                }))
              }
            >
              <option value="">{siteTexts.common.sex.notInformed}</option>
              <option value="female">{siteTexts.common.sex.female}</option>
              <option value="male">{siteTexts.common.sex.male}</option>
              <option value="other">{siteTexts.common.sex.other}</option>
            </select>
          </label>
          <label className="field">
            <span>{t.labels.category}</span>
            <select
              value={newUser.category}
              onChange={(event) =>
                setNewUser((prev) => ({
                  ...prev,
                  category: event.target.value as CreateUserPayload["category"],
                }))
              }
            >
              {USER_CATEGORY_VALUES.map((item) => (
                <option
                  key={`admin-create-user-category-${item || "empty"}`}
                  value={item}
                >
                  {item
                    ? userCategoryLabel(item)
                    : siteTexts.common.category.notInformed}
                </option>
              ))}
            </select>
          </label>
          {isAdmin && (
            <label className="field">
              <span>{t.labels.profile}</span>
              <select
                value={newUser.role}
                onChange={(event) =>
                  setNewUser((prev) => ({
                    ...prev,
                    role: event.target.value as CreateUserPayload["role"],
                  }))
                }
              >
                <option value="user">{t.statuses.user}</option>
                <option value="organizer">{t.statuses.organizer}</option>
                <option value="admin">{t.statuses.administrator}</option>
              </select>
            </label>
          )}
          <div className="admin-modal-actions">
            <Button type="button" variant="pill" onClick={onCancel}>
              {siteTexts.common.cancel}
            </Button>
            <Button type="submit" variant="primary">
              {t.actions.createUser}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
